/* eslint-disable max-lines */
import {useUser} from '@gaia/stores/user';
import cleanUserLayers from '@gaia/utils/gaia/cleanUserLayers';
import {fetchDefaultUserLayers} from '@gaia/utils/gaia/fetchDefaultUserLayers';
import {fetchLayerTypeCategories} from '@gaia/utils/gaia/fetchLayerTypeCategories';
import {fetchLayerTypes} from '@gaia/utils/gaia/fetchLayerTypes';
import {fetchUserLayers} from '@gaia/utils/gaia/fetchUserLayers';
import isCustomLayer from '@gaia/utils/gaia/isCustomLayer';
import saveCustomMapSource from '@gaia/utils/gaia/saveCustomMapSource';
import saveUserLayers from '@gaia/utils/gaia/saveUserLayers';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import trackLayerAnalytics from '@gaia/utils/gaia/trackLayerAnalytics';
import {useLayeredModeState} from '@map/sidebars/LayersSidebar/store';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import uniq from 'lodash/uniq';
import {useCallback, useEffect, useMemo, useState} from 'react';

import useIsSaved from './useIsSaved';
import {useLatest} from './useLatest';

interface AddAction {
    type: 'add';
    layer: LayerType;
}
interface UpdateAction {
    type: 'update';
    layers: UserLayer[];
}
interface RemoveAction {
    type: 'remove';
    layer: LayerType;
}

interface AddCustomLayerAction {
    type: 'addCustomLayer';
    layer: LayerType;
}

interface PreviewCustomLayerAction {
    type: 'previewCustomLayer';
    layer: LayerType;
}

type Action =
    | AddAction
    | UpdateAction
    | RemoveAction
    | AddCustomLayerAction
    | PreviewCustomLayerAction;

interface State {
    defaultUserLayers: UserLayer[] | undefined;
    layerTypes: {[key: string]: LayerType} | undefined;
    isUserLayersEphemeral: boolean;
    userLayers: UserLayer[] | undefined;
    layerTypeCategories: LayerTypeCategory[] | undefined;
}

export type UserLayerDispatcher = (action: Action) => void;

export type UseLayersOutput = [
    {
        layers: Layer[] | undefined;
        userLayers: UserLayer[] | undefined;
        layerTypes: Record<string, LayerType> | undefined;
        layerTypeCategories: LayerTypeCategory[] | undefined;
    },
    UserLayerDispatcher,
    number
];

function isCustomLayerPreview(layer: LayerType) {
    return layer.id === 'preview';
}

export function useLayers(
    propsLayers: Layer[] | undefined,
    urlPropsLayers: string[] | undefined
): UseLayersOutput {
    const {hasSubscription, isAuthenticated, canAccessNonPublicSources} = useUser();
    const isLayered = useLayeredModeState();

    const [state, setState] = useState<State>({
        defaultUserLayers: undefined,
        layerTypes: undefined,
        isUserLayersEphemeral: false,
        userLayers: undefined,
        layerTypeCategories: [],
    });
    const [savedUserLayers, setSavedUserLayers] = useState<UserLayer[] | undefined>();
    const [erroredUserLayers, setErroredUserLayers] = useState<UserLayer[] | undefined>();
    const [unsaved, addSavePromise] = useIsSaved();

    const save = useCallback(
        async (
            nextUserLayers: UserLayer[],
            layerTypes?: State['layerTypes'],
            layerTypeCategories?: State['layerTypeCategories']
        ) => {
            const categories = layerTypeCategories || state.layerTypeCategories;
            setState({
                ...state,
                userLayers: nextUserLayers,
                layerTypes: layerTypes || state.layerTypes,
                layerTypeCategories: categories,
            });
            const customImports = categories?.find((cat) => cat.id === 'custom')?.sources;
            try {
                setSavedUserLayers(
                    await saveUserLayers({
                        userLayers: nextUserLayers,
                        savedUserLayers,
                        erroredUserLayers,
                        addSavePromise,
                        isAuthenticated,
                        customImports,
                    })
                );
            } catch (e) {
                setErroredUserLayers(nextUserLayers);
                throw e;
            }
            return;
        },
        [addSavePromise, erroredUserLayers, isAuthenticated, savedUserLayers, state]
    );

    const add = useCallback(
        (action: AddAction) => {
            if (!state.layerTypes || !state.defaultUserLayers || !state.userLayers) return;
            const layerType = action.layer.id;
            const opacity = state.layerTypes[layerType]?.default_opacity ?? 1;
            const nextUserLayers = cleanUserLayers({
                userLayers: [
                    ...state.userLayers,
                    {
                        layer: {
                            opacity,
                            type: layerType,
                        },
                        isVisible: true,
                    },
                ],
                isLayered,
                layerTypes: state.layerTypes,
                defaultUserLayers: state.defaultUserLayers,
            });
            if (nextUserLayers && !isCustomLayer(state.layerTypeCategories, layerType)) {
                return save(nextUserLayers);
            }
        },
        [state, isLayered, save]
    );

    const update = useCallback(
        (action: UpdateAction) => {
            if (!state.layerTypes || !state.defaultUserLayers) return;
            const nextUserLayers = cleanUserLayers({
                userLayers: action.layers,
                isLayered,
                layerTypes: state.layerTypes,
                defaultUserLayers: state.defaultUserLayers,
            });
            if (nextUserLayers) {
                return save(nextUserLayers);
            }
        },
        [isLayered, save, state.defaultUserLayers, state.layerTypes]
    );

    const remove = useCallback(
        (action: RemoveAction) => {
            if (!state.layerTypes || !state.defaultUserLayers || !state.userLayers) return;
            const nextUserLayers = cleanUserLayers({
                userLayers: state.userLayers.filter(
                    (layer: UserLayer) => action.layer.id !== layer.layer.type
                ),
                isLayered,
                layerTypes: state.layerTypes,
                defaultUserLayers: state.defaultUserLayers,
            });

            let layerTypes = state.layerTypes;

            if (isCustomLayerPreview(action.layer)) {
                layerTypes = pickBy(
                    layerTypes,
                    (otherLayerType) => otherLayerType !== action.layer
                );
            } else if (isCustomLayer(state.layerTypeCategories, action.layer.id)) {
                const message =
                    `This will delete "${action.layer.name}" from your account. ` +
                    'Do you wish to continue?';

                if (!confirm(message)) {
                    return action.layer;
                }

                layerTypes = pickBy(
                    layerTypes,
                    (otherLayerType) => otherLayerType !== action.layer
                );
            }
            if (nextUserLayers) {
                return save(nextUserLayers, layerTypes);
            }
        },
        [isLayered, save, state]
    );

    const previewCustomLayer = useCallback(
        (action: PreviewCustomLayerAction) => {
            if (!state.layerTypes || !state.userLayers) return;
            const nextLayerTypes = {
                ...state.layerTypes,
                preview: action.layer,
            };

            const nextUserLayers = cleanUserLayers({
                userLayers: [
                    ...state.userLayers,
                    {
                        layer: {opacity: 1, type: 'preview'},
                        isVisible: true,
                    },
                ],
                isLayered: true,
                layerTypes: state.layerTypes,
                defaultUserLayers: state.defaultUserLayers,
            });

            ampli.previewCustomMapSource({
                category: 'map2',
                minZoom: action.layer.minzoom,
                maxZoom: action.layer.maxzoom,
                reverseY: Boolean(action.layer.reverseY),
                tileUrl: action.layer.tiles[0],
            });

            if (nextUserLayers) {
                setState({
                    ...state,
                    userLayers: nextUserLayers,
                    layerTypes: nextLayerTypes,
                });
            }
        },
        [state]
    );

    const addCustomLayer = useCallback(
        async (action: AddCustomLayerAction) => {
            if (
                !state.layerTypes ||
                !state.defaultUserLayers ||
                !state.userLayers ||
                !state.layerTypeCategories
            )
                return;

            const response = await saveCustomMapSource({
                notes: action.layer.description,
                title: action.layer.name || 'Custom Map Source',
                tile_url: action.layer.tiles[0],
                min_zoom: action.layer.minzoom,
                max_zoom: action.layer.maxzoom,
                reversed_y_axis: action.layer.reverseY!,
            });

            const newCustomLayer = {
                ...action.layer,
                description: response.notes,
                id: response.unique_cache_key,
                name: response.title,
            };

            const defaultCategory: LayerTypeCategory = {
                name: 'Custom Imports',
                id: 'custom',
                rank: 100,
                description: '',
                icon: '',
                iconURL: '',
                app: 'gaiagps-ng-2019',
                sources: [],
            };
            let nextLayerTypeCategories: LayerTypeCategory[];
            if (state.layerTypeCategories.find((category) => category.id === 'custom')) {
                // Display the new custom layer under the Custom Imports sidebar menu
                nextLayerTypeCategories = state.layerTypeCategories.map((category) => {
                    if (category.id === 'custom') {
                        return {
                            ...category,
                            sources: uniq([...category.sources, newCustomLayer.id]),
                        };
                    }
                    return category;
                });
            } else {
                // Custom category won't exist if a user has no previously-saved sources
                nextLayerTypeCategories = [
                    ...state.layerTypeCategories,
                    {
                        ...defaultCategory,
                        sources: [newCustomLayer.id],
                    },
                ];
            }

            const nextLayerTypes = {
                ...omit(state.layerTypes, 'preview'),
                [newCustomLayer.id]: newCustomLayer,
            };

            // Remove the preview layer, if it exists, and add the new layer id
            const updatedUserLayers = [
                ...state.userLayers.filter((layer) => layer.layer.type !== 'preview'),
                {
                    layer: {opacity: 1, type: newCustomLayer.id},
                    isVisible: true,
                },
            ];

            const nextUserLayers = cleanUserLayers({
                userLayers: updatedUserLayers,
                isLayered,
                defaultUserLayers: state.defaultUserLayers,
                layerTypes: nextLayerTypes,
            });

            ampli.saveCustomMapSource({
                category: 'map2',
                layerName: newCustomLayer.name,
                minZoom: newCustomLayer.minzoom,
                maxZoom: newCustomLayer.maxzoom,
                reverseY: Boolean(newCustomLayer.reverseY),
                tileUrl: newCustomLayer.tiles[0],
            });

            if (nextUserLayers) {
                setState({
                    ...state,
                    userLayers: nextUserLayers,
                    layerTypes: nextLayerTypes,
                    layerTypeCategories: nextLayerTypeCategories,
                });
            }
        },
        [state, isLayered]
    );

    const dispatcher = useCallback(
        (action: Action) => {
            if (unsaved) return;

            if (action.type === 'add') return add(action);
            else if (action.type === 'update') return update(action);
            else if (action.type === 'remove') return remove(action);
            else if (action.type === 'previewCustomLayer') return previewCustomLayer(action);
            else if (action.type === 'addCustomLayer') return addCustomLayer(action);
            throw new Error('Unknown layer action');
        },
        [add, unsaved, remove, update, addCustomLayer, previewCustomLayer]
    );

    const layers = useMemo(
        () =>
            propsLayers
                ? propsLayers
                : state.userLayers
                ? state.userLayers
                      .filter((userLayer: UserLayer) => userLayer.isVisible)
                      .map((userLayer: UserLayer) => userLayer.layer)
                : undefined,
        [propsLayers, state.userLayers]
    );

    const latest = useLatest({state, urlPropsLayers});

    useEffect(() => {
        Promise.all([
            fetchUserLayers({
                urlLayers: latest.current.urlPropsLayers,
                hasSubscription,
                canAccessNonPublicSources,
                isAuthenticated,
            }),
            fetchDefaultUserLayers({hasSubscription, canAccessNonPublicSources}),
            fetchLayerTypes({isAuthenticated, canAccessNonPublicSources}),
            fetchLayerTypeCategories({isAuthenticated, canAccessNonPublicSources}),
        ])
            .then(([initialUserLayers, defaultUserLayers, layerTypes, layerTypeCategories]) => {
                setSavedUserLayers(initialUserLayers?.savedUserLayers);
                setState({
                    ...latest.current.state,
                    defaultUserLayers,
                    layerTypes,
                    layerTypeCategories,
                    isUserLayersEphemeral: Boolean(initialUserLayers?.isUserLayersEphemeral),
                    userLayers: cleanUserLayers({
                        userLayers: initialUserLayers?.userLayers,
                        isLayered,
                        defaultUserLayers,
                        layerTypes,
                    }),
                });

                trackLayerAnalytics(isAuthenticated, initialUserLayers?.userLayers);
            })
            .catch(console.warn);
    }, [canAccessNonPublicSources, hasSubscription, isAuthenticated, latest, isLayered]);

    return [
        {
            userLayers: state.userLayers,
            layerTypes: state.layerTypes,
            layers,
            layerTypeCategories: state.layerTypeCategories,
        },
        dispatcher,
        unsaved,
    ];
}
